/**
 * Google Tag Manager Service
 * 
 * This service provides utilities for interacting with Google Tag Manager,
 * including initialization, event tracking, and data layer management.
 */

import TagManager from 'react-gtm-module';
import analyticsConfig from '../config/analyticsConfig';

class GTMService {
  constructor() {
    this.isInitialized = false;
    this.config = analyticsConfig;
  }

  /**
   * Initialize Google Tag Manager
   * @param {string} gtmId - Optional GTM container ID (defaults to config)
   * @param {Object} options - Optional GTM initialization options
   */
  initialize(gtmId = null, options = {}) {
    try {
      const containerId = gtmId || this.config.gtm.containerId;
      
      if (!containerId || containerId === 'GTM-YOUR-CONTAINER-ID') {
        console.warn('GTM: No valid container ID provided');
        return false;
      }

      const gtmArgs = {
        gtmId: containerId,
        ...this.config.gtm.options,
        ...options
      };

      TagManager.initialize(gtmArgs);
      this.isInitialized = true;

      if (this.config.debug.enableLogging) {
        console.log(`GTM initialized successfully with ID: ${containerId}`, gtmArgs);
      }

      // Send initial page view
      this.trackPageView(window.location.pathname, document.title);

      return true;
    } catch (error) {
      console.error('GTM initialization failed:', error);
      return false;
    }
  }

  /**
   * Check if GTM is ready for tracking
   * @returns {boolean}
   */
  isReady() {
    return this.isInitialized && typeof window !== 'undefined' && window.dataLayer;
  }

  /**
   * Send a custom event to GTM using direct dataLayer.push()
   * @param {string} event - Event name
   * @param {Object} data - Event data
   */
  trackEvent(event, data = {}) {
    if (!this.isReady()) {
      if (this.config.debug.enableLogging) {
        console.warn('GTM: Service not ready, skipping event:', event);
      }
      return;
    }

    if (!this.config.events[event] && this.config.events[event] !== undefined) {
      if (this.config.debug.enableLogging) {
        console.log(`GTM: Event '${event}' is disabled in config`);
      }
      return;
    }

    // Use the standard GTM dataLayer format that GTM can properly read
    const eventData = {
      event,
      timestamp: new Date().toISOString(),
      ...data
    };

    // Push directly to dataLayer using the standard format
    // This ensures GTM variables can resolve correctly in Tag Assistant
    if (window.dataLayer) {
      window.dataLayer.push(eventData);
    } else {
      // Fallback to react-gtm-module if dataLayer is not available
      TagManager.dataLayer(eventData);
    }

    // Enhanced debugging to show exactly what's being pushed
    if (this.config.debug.logGTMEvents) {
      console.log('🏷️ GTM Event pushed to dataLayer:', eventData);
      console.log('📊 DataLayer contents:', window.dataLayer);
    }
  }

  /**
   * Track page views
   * @param {string} path - Page path
   * @param {string} title - Page title
   * @param {Object} additionalData - Additional page data
   */
  trackPageView(path, title, additionalData = {}) {
    if (!this.config.events.pageViews) return;

    const pageCategory = this.getPageCategory(path);
    
    this.trackEvent('page_view', {
      page_path: path,
      page_title: title,
      page_category: pageCategory,
      page_location: window.location.href,
      ...additionalData
    });
  }

  /**
   * Track user dimension updates (when user analytics data changes)
   * @param {Object} userDimensions - User dimension data
   */
  trackUserDimensions(userDimensions) {
    if (!this.config.events.userDimensions) return;

    // Use the exact format GTM expects for proper variable resolution
    const eventData = {
      event: 'custom_dimensions_event',
      event_category: 'user_analytics',
      user_name: userDimensions.user_name || 'unknown',
      user_email: userDimensions.user_email || 'unknown',
      user_role: userDimensions.user_role || 'unknown',
      user_id: userDimensions.user_id || 'unknown',
      user_cas_name: userDimensions.user_cas_name || 'unknown',
      user_client_name: userDimensions.user_client_name || 'unknown',
      user_institution_name: userDimensions.user_institution_name || 'unknown',
      last_active_date: userDimensions.last_active_date || new Date().toISOString(),
      timestamp: new Date().toISOString(),
      // Include any additional dimensions passed
      ...userDimensions
    };

    // Push directly to dataLayer for GTM variable compatibility
    if (window.dataLayer) {
      window.dataLayer.push(eventData);
    }

    if (this.config.debug.logGTMEvents) {
      console.log('🏷️ GTM User Dimensions Event:', eventData);
    }
  }

  /**
   * Track report generation events
   * @param {string} reportType - Type of report being generated
   * @param {Object} reportData - Additional report metadata
   */
  trackReportGeneration(reportType, reportData = {}) {
    if (!this.config.events.reportGeneration) return;

    this.trackEvent('generate_report', {
      event_category: 'report',
      report_type: reportType,
      ...reportData
    });
  }

  /**
   * Track report export events
   * @param {string} format - Export format (PDF, Excel, etc.)
   * @param {string} reportType - Type of report being exported
   * @param {Object} exportData - Additional export metadata
   */
  trackReportExport(format, reportType, exportData = {}) {
    if (!this.config.events.reportExport) return;

    this.trackEvent('export_report', {
      event_category: 'report',
      export_format: format,
      report_type: reportType,
      ...exportData
    });
  }

  /**
   * Track user login events
   * @param {Object} loginData - Login event data
   */
  trackUserLogin(loginData = {}) {
    if (!this.config.events.userLogin) return;

    this.trackEvent('login', {
      event_category: 'authentication',
      method: 'form_login',
      ...loginData
    });
  }

  /**
   * Track user logout events
   * @param {Object} logoutData - Logout event data
   */
  trackUserLogout(logoutData = {}) {
    if (!this.config.events.userLogout) return;

    this.trackEvent('logout', {
      event_category: 'authentication',
      ...logoutData
    });
  }

  /**
   * Track navigation events (menu clicks, etc.)
   * @param {string} destination - Where the user is navigating
   * @param {string} source - Where the navigation originated from
   * @param {Object} navigationData - Additional navigation data
   */
  trackNavigation(destination, source, navigationData = {}) {
    if (!this.config.events.navigation) return;

    this.trackEvent('navigate', {
      event_category: 'navigation',
      destination,
      source,
      ...navigationData
    });
  }

  /**
   * Track search events
   * @param {string} searchTerm - What the user searched for
   * @param {number} resultCount - Number of results returned
   * @param {Object} searchData - Additional search metadata
   */
  trackSearch(searchTerm, resultCount, searchData = {}) {
    if (!this.config.events.search) return;

    this.trackEvent('search', {
      event_category: 'search',
      search_term: searchTerm,
      result_count: resultCount,
      ...searchData
    });
  }

  /**
   * Track form submission events
   * @param {string} formName - Name/ID of the form
   * @param {string} formAction - What the form does
   * @param {Object} formData - Additional form data
   */
  trackFormSubmission(formName, formAction, formData = {}) {
    if (!this.config.events.formSubmission) return;

    this.trackEvent('form_submit', {
      event_category: 'form',
      form_name: formName,
      form_action: formAction,
      ...formData
    });
  }

  /**
   * Determine page category based on path
   * @param {string} path - Current page path
   * @returns {string} Page category
   */
  getPageCategory(path) {
    if (path.includes('/login') || path.includes('/signup')) {
      return 'public';
    }
    if (path.includes('/demo')) {
      return 'demo';
    }
    return 'private';
  }

  /**
   * Get the current configuration
   * @returns {Object} Current analytics configuration
   */
  getConfig() {
    return this.config;
  }

  /**
   * Update configuration settings
   * @param {Object} newConfig - New configuration to merge
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Test method to verify dataLayer format
   * This pushes a test event to help debug GTM variable resolution
   */
  testDataLayerFormat() {
    if (!this.isReady()) {
      console.warn('GTM: Service not ready for testing');
      return;
    }

    const testEvent = {
      event: 'test_custom_dimensions_event',
      user_name: 'Test User',
      user_email: 'test@example.com',
      user_role: 'Test Role',
      user_id: 'test_123',
      user_cas_name: 'Test CAS',
      timestamp: new Date().toISOString()
    };

    console.log('🧪 Testing GTM dataLayer format...');
    console.log('📤 Pushing test event:', testEvent);
    
    window.dataLayer.push(testEvent);
    
    console.log('📊 Current dataLayer:', window.dataLayer);
    console.log('✅ Test event pushed. Check GTM Tag Assistant for variable resolution.');
    
    return testEvent;
  }
}

// Export a singleton instance
const gtmService = new GTMService();
export default gtmService;
